/**
 * PaymentPathModal.jsx
 * ====================
 *
 * Modale intercalaire obligatoire lors de la souscription entreprise.
 * Propose deux chemins commerciaux clairs :
 *   1. Commander immédiatement en ligne (paiement direct Stripe)
 *   2. Demander un devis personnalisé (flux commercial)
 *
 * Vague 1 du workflow entreprise premium.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, FileText, ArrowRight, X, Sparkles, Building2 } from 'lucide-react';

const typeLabels = {
  ia: {
    title: "Coaching IA",
    directRoute: "/subscription/enterprise/ia",
    quoteRoute: "/subscription/enterprise/ia/quote-request",
    accent: "#92710A",
    accentSoft: "#F5F0E3",
    accentBorder: "#E8DFC8",
  },
  human: {
    title: "Coaching Humain",
    directRoute: "/subscription/enterprise/coaching-humain",
    quoteRoute: "/subscription/enterprise/coaching-humain/quote-request",
    accent: "#059669",
    accentSoft: "#ECFDF5",
    accentBorder: "#D1FAE5",
  },
};

export default function PaymentPathModal({ isOpen, onClose, offerType }) {
  const navigate = useNavigate();
  if (!offerType) return null;

  const labels = typeLabels[offerType];

  const goDirect = () => {
    onClose?.();
    navigate(labels.directRoute);
  };
  const goQuote = () => {
    onClose?.();
    navigate(labels.quoteRoute);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          data-testid="payment-path-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={onClose}
              data-testid="payment-path-modal-close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full bg-[#F5F5F2] hover:bg-[#EBEAE3] flex items-center justify-center transition-colors z-10"
              aria-label="Fermer"
            >
              <X className="w-4 h-4 text-[#4B5563]" />
            </button>

            {/* Header */}
            <div className="px-8 pt-10 pb-6 border-b border-[#E8E5DE]">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-[#92710A]" />
                <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">
                  Souscription — {labels.title}
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight">
                Comment souhaitez-vous souscrire&nbsp;?
              </h2>
              <p className="text-[#4B5563] text-sm mt-2 leading-relaxed">
                Choisissez le parcours le mieux adapté à votre structure et à votre processus d'achat.
              </p>
            </div>

            {/* Options */}
            <div className="grid md:grid-cols-2 gap-4 p-6">
              {/* Option A — Commander immédiatement */}
              <button
                onClick={goDirect}
                data-testid="payment-path-direct"
                className="group text-left p-6 rounded-2xl border-2 border-[#E8E5DE] hover:border-[#92710A] hover:bg-[#FBF9F3] transition-all duration-200"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: labels.accentSoft, border: `1px solid ${labels.accentBorder}` }}
                >
                  <CreditCard className="w-5 h-5" style={{ color: labels.accent }} />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-display font-medium text-[#111827]">
                    Commander immédiatement en&nbsp;ligne
                  </h3>
                </div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#92710A] font-medium mb-3">
                  Recommandé &lt; 50 collaborateurs
                </p>
                <ul className="space-y-1.5 mb-5 text-[13px] text-[#4B5563] leading-relaxed">
                  <li>• Mise en service sous 5 minutes</li>
                  <li>• Paiement sécurisé par carte bancaire</li>
                  <li>• Facture émise automatiquement</li>
                </ul>
                <div className="flex items-center gap-1.5 text-[#92710A] text-sm font-medium group-hover:gap-2.5 transition-all">
                  Continuer en paiement direct
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>

              {/* Option B — Demander un devis (Iter294 — fond clair harmonisé) */}
              <button
                onClick={goQuote}
                data-testid="payment-path-quote"
                className="group text-left p-6 rounded-2xl border-2 border-[#E8E5DE] hover:border-[#D4AF37] hover:bg-[#FBF9F3] transition-all duration-200 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 bg-[#1F1A12] text-[#F3D060] text-[10px] font-display font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm">
                  Premium
                </div>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: labels.accentSoft, border: `1px solid ${labels.accentBorder}` }}
                >
                  <FileText className="w-5 h-5" style={{ color: labels.accent }} />
                </div>
                <h3 className="text-base font-display font-medium text-[#111827] mb-1">
                  Demander un devis personnalisé
                </h3>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#92710A] font-medium mb-3">
                  Adapté dès 50+ collaborateurs
                </p>
                <ul className="space-y-1.5 mb-5 text-[13px] text-[#4B5563] leading-relaxed">
                  <li>• Offre sur-mesure négociée</li>
                  <li>• Bon de commande &amp; facturation classique</li>
                  <li>• Accompagnement commercial dédié</li>
                </ul>
                <div className="flex items-center gap-1.5 text-[#92710A] text-sm font-medium group-hover:gap-2.5 transition-all">
                  <Sparkles className="w-3.5 h-3.5" />
                  Ouvrir le formulaire de devis
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="px-8 pb-6 pt-2">
              <p className="text-[11px] text-[#6B7280] text-center">
                Vous pourrez changer de parcours à tout moment depuis votre espace entreprise.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
